/**
 * English catalog — a full translation of the externalised tranche.
 *
 * Declared `: TranslationKeys`, so TypeScript enforces exact structural parity
 * with the French source (`fr.ts`): a missing key or an unknown extra key fails
 * the build. Any string not yet externalised elsewhere in the app still falls
 * back to French at runtime (see FALLBACK_LOCALE / useT).
 */

import type { TranslationKeys } from './fr';

const en: TranslationKeys = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    create: 'Create',
    edit: 'Edit',
    search: 'Search',
    loading: 'Loading…',
    confirm: 'Confirm',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    add: 'Add',
    remove: 'Remove',
    apply: 'Apply',
    reset: 'Reset',
    refresh: 'Refresh',
    retry: 'Retry',
    duplicate: 'Duplicate',
    export: 'Export',
    import: 'Import',
    copy: 'Copy',
    download: 'Download',
    upload: 'Upload',
    filter: 'Filter',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    actions: 'Actions',
    more: 'More',
    settings: 'Settings',
    processing: 'Processing…',
    readOnly: 'Read-only',
    empty: 'No items',
  },

  nav: {
    brand: 'PubSocialNet',
    groups: {
      general: 'General',
      creation: 'Creation',
      prospection: 'Prospecting',
      ai: 'AI',
      system: 'System',
    },
    items: {
      dashboard: 'Dashboard',
      content: 'Content',
      calendar: 'Calendar',
      publishing: 'Publishing',
      contentQueue: 'Content Queue',
      bulkSchedule: 'Bulk Scheduling',
      platforms: 'Platforms',
      ads: 'Ads',
      mediaLibrary: 'Media library',
      analytics: 'Analytics',
      design: 'Design',
      workflows: 'Workflows',
      crm: 'CRM & Leads',
      discovery: 'Discovery',
      prospectionRecommend: 'Recommendations',
      prospectionCalendar: 'Prospection calendar',
      accountIntelligence: 'Account intelligence',
      carouselEditor: 'Carousel editor',
      richContentStudio: 'Rich content studio',
      prospect: 'B2B database',
      marketplace: 'Marketplace',
      social: 'Social leads',
      outreach: 'Email outreach',
      inbox: 'Social inbox',
      community: 'Community',
      listening: 'Social listening',
      bio: 'Link-in-bio',
      projects: 'Projects',
      aiTeam: 'AI team',
      aiOffice: 'AI office',
      aiTeams: 'AI teams',
      aiDeliverables: 'AI deliverables',
      aiSchedules: 'AI schedules',
      aiControl: 'AI control',
      aiAuditTrail: 'AI audit trail',
      staffQuestions: 'Agent questions',
      grants: 'Grant requests',
      superadminFleet: 'Platform fleet',
      orchestration: 'AI orchestration',
      aiAssistant: 'AI assistant',
      contentStudio: 'Content studio',
      ghostwriter: 'AI ghostwriter',
      repurpose: 'Repurposing',
      hookLibrary: 'Hook library',
      brandVoice: 'Brand voice',
      abCopy: 'A/B copy tests',
      suggestions: 'AI suggestions',
      personalization: 'Personalization',
      communityAi: 'Community AI',
      ragDocuments: 'RAG documents',
      memory: 'AI memory',
      approvals: 'Approvals',
      aiActivity: 'AI activity',
      settings: 'Settings',
    },
    header: {
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      toggleSidebar: 'Collapse or expand the sidebar',
      breadcrumbRoot: 'PubSocialNet',
      breadcrumbAria: 'Breadcrumb',
      searchPlaceholder: 'Search…',
      openCommandPalette: 'Open the command palette',
      quickActions: 'Quick actions',
      create: 'Create',
      createItem: 'Create an item',
      shortcuts: 'Keyboard shortcuts',
      showShortcuts: 'Show keyboard shortcuts',
    },
    quickCreate: {
      newContent: 'New content',
      newContact: 'New contact',
      newCompany: 'New company',
      newCampaign: 'New campaign',
      newProject: 'New project',
      newDesign: 'New design',
    },
    quickAccess: {
      search: 'Search (⌘K)',
      newContent: 'New content',
      newContact: 'New contact',
      newDeal: 'New deal',
      inbox: 'Social inbox',
      assistant: 'AI assistant',
    },
    user: {
      fallbackName: 'User',
      connectedAccount: 'Connected account',
      settings: 'Settings',
      signout: 'Sign out',
    },
    footer: {
      environment: 'Runtime environment',
      whatsNew: "What's new",
      shortcuts: 'Shortcuts',
      privacy: 'Privacy',
      links: 'Footer links',
      statusTitle: 'Service status (/health polled every 30 s)',
      operational: 'Operational',
      unavailable: 'Service unavailable',
      checking: 'Checking…',
    },
    primaryNav: 'Primary navigation',
  },

  settings: {
    title: 'Settings',
    subtitle:
      'AI credits & billing, agent and model registry, team, profile and social connections.',
    navAria: 'Settings navigation',
    tabs: {
      credits: 'Credits & Billing',
      subscription: 'Subscription',
      referral: 'Referral',
      aiRegistry: 'AI registry',
      models: 'AI models',
      governance: 'AI governance',
      trustCenter: 'Trust Center',
      organization: 'Organization',
      notifications: 'Notifications',
      roles: 'Roles & permissions',
      connectors: 'Connectors',
      developers: 'Developer API',
      integrations: 'Integrations',
      connections: 'Connected accounts',
      team: 'Team',
      profile: 'Profile',
    },
  },

  profile: {
    tabsAria: 'Profile sections',
    tabProfile: 'Profile',
    tabConnections: 'Connections',
    emailVerified: 'Email verified',
    emailUnverified: 'Email not verified',
    noRole: 'no role',
    stats: {
      roles: 'Roles',
      session: 'Session',
      expiresAt: 'expires at',
    },
    account: {
      title: 'Account',
      name: 'Name',
      email: 'Email',
      username: 'Username',
      tenant: 'Tenant',
      userId: 'User ID',
      roles: 'Roles',
    },
    prefs: {
      title: 'Preferences',
      language: 'Language',
      languageHint: 'Preference remembered on this device.',
      theme: 'Theme',
      themeHint: '“System” follows the browser’s light/dark setting.',
      themeSystem: 'System',
      themeLight: 'Light',
      themeDark: 'Dark',
      appearance: 'Appearance',
      appearanceHint: 'Light/dark toggle and accent color — also available from the header.',
    },
  },

  platforms: {
    connections: {
      needsReconnectBadge: 'Reconnect required',
      needsReconnectHint:
        'The stored token can no longer be read on the server — this connection can no longer publish. Reconnect the account to fix it.',
      reconnectAction: 'Reconnect',
    },
  },

  publishingWizard: {
    pageTitle: 'AI Assistant — post creation',
    pageSubtitle:
      'Generate a post step by step (theme → title → description → detailed text), with an AI review at every stage, then send it to the composer.',
    backToComposer: 'Back to composer',
    stages: {
      theme: {
        label: 'Theme',
        fieldLabel: 'Content theme',
        placeholder: 'E.g. product launch, HR tips, case study…',
        hint: 'The theme drives every following stage.',
      },
      title: {
        label: 'Title',
        fieldLabel: 'Title / hook',
        placeholder: 'A catchy title…',
        hint: 'Pick a candidate or edit the post title.',
      },
      description: {
        label: 'Description',
        fieldLabel: 'Description / summary',
        placeholder: 'A one- or two-sentence summary…',
        hint: 'A short summary expanded in the next stage.',
      },
      detail: {
        label: 'Detailed text',
        fieldLabel: 'Full post text',
        placeholder: 'The full text, ready to publish…',
        hint: 'The final text sent to the composer.',
      },
    },
    seedLabel: 'Keywords / context (optional)',
    seedHint: 'Helps theme generation — industry, audience, desired tone…',
    seedPlaceholder: 'E.g. industry, audience, desired tone…',
    platformLabel: 'Platform (context)',
    generate: 'Generate candidates',
    regenerate: 'Regenerate candidates',
    generating: 'Generating…',
    candidatesTitle: 'Candidates',
    noCandidatesError: 'No candidates returned — type your text directly below.',
    genericError: 'Generation failed — retry or type your text manually.',
    modelLabel: 'Model',
    reviewSectionTitle: '🧠 AI review of this stage',
    back: 'Previous stage',
    next: 'Next stage',
    sendToComposer: 'Send to composer',
    sentToComposer: 'Post sent to the composer.',
    emptyStageError: 'Fill in or generate text before continuing.',
    quickCreateCta: 'Generate with AI',
  },

  publishingSource: {
    label: 'Content source',
    aiGenerated: 'AI (staged assistant)',
    aiGeneratedHint: 'Open the AI assistant to generate the content step by step, then send it here.',
    aiOpenWizard: 'Open the AI assistant',
    manualExternal: 'Manual — external media',
    manualExternalHint:
      'Hand-written with externally-sourced media (upload, library, AI image or URL import).',
    manualDesign: 'Manual — graphic design',
    manualDesignHint: 'Hand-written with a visual created in the graphic designer.',
    designAttached: 'Designer visual attached to the post.',
  },

  publishingTracking: {
    pageTitle: 'Post tracking',
    pageSubtitle: 'Metrics history, trend and post-publication actions for every published post.',
    pickerLabel: 'Published posts',
    pickerEmpty: 'No published post yet.',
    historyTitle: 'Metrics history',
    historyEmpty: 'No metrics snapshot recorded for this post.',
    timeseriesTitle: 'Overall trend (last 30 days)',
    timeseriesEmpty: 'Not enough data to plot a trend.',
    trackingUnavailable: 'Tracking is not available on this environment yet.',
    refresh: 'Refresh metrics',
    refreshing: 'Refreshing…',
    refreshed: 'Metrics refreshed.',
    actionsTitle: 'Actions',
    actionBoost: 'Boost',
    actionReshare: 'Reshare',
    actionReply: 'Reply',
    actionRetry: 'Retry',
    actionReschedule: 'Reschedule',
    replyPlaceholder: 'Your reply…',
    replySend: 'Send reply',
    rescheduleLabel: 'New date and time',
    rescheduleConfirm: 'Reschedule',
    actionUnsupported: 'Action not supported for this platform or account.',
    actionSuccess: 'Action completed successfully.',
    actionError: 'Action failed — please retry.',
    openTracking: 'Tracking & actions',
    engagementTitle: 'Engagement',
    likes: 'Likes',
    comments: 'Comments',
    shares: 'Shares',
    reach: 'Reach',
    impressions: 'Impressions',
    capturedAt: 'Captured on',
    noJobContext: 'Job details unknown (direct access) — some actions are hidden.',
  },
};

export default en;
